/**
 * Route guard.
 *
 * Redirects to `/login` and remembers where the user was headed, so signing in
 * returns them to the page they asked for instead of always landing on the
 * dashboard. The check is on `session` presence only — authorisation is the
 * API's job and it re-checks every request regardless of what the UI shows.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSession } from '../session/session-context';

export function RequireSession({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // A held forced-change flag outranks whatever the user asked for. The API
  // refuses these routes with 409 regardless, so navigating to the dashboard would
  // land them on a page whose every request fails — and the reason would be a
  // status code rather than an instruction.
  //
  // `replace` so the blocked URL is not left in history: pressing Back would
  // otherwise bounce between the two pages.
  if (session.user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}